/**
 * FP3-064 — Bulk Assign Campus dialog.
 *
 * Surfaced from Caseload's bulk action toolbar. Lets admin family +
 * solo provider OWNER reassign N selected students (≤500) to one
 * active school/campus in their scoped org. Server enforces
 * cross-org rejection — rows from another org get skipped with
 * status_code in the response, never silently moved.
 *
 * Test ids:
 *   - `assign-campus-dialog`
 *   - `assign-campus-school-select`
 *   - `assign-campus-cancel`
 *   - `assign-campus-confirm`
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

export default function AssignCampusDialog({
  open, onOpenChange, studentIds, schools, onAssigned,
}) {
  const [schoolId, setSchoolId] = useState("");
  const [saving, setSaving] = useState(false);

  // Active (non-archived) schools only. The bulk endpoint also
  // refuses an archived target, but pre-filtering keeps the UI
  // honest.
  const activeSchools = (schools || []).filter((s) => !s.is_archived);

  useEffect(() => {
    if (open) {
      // Pre-select the first active school when there's only one —
      // matches Pete's primary-workspace flow (no decision needed).
      if (activeSchools.length === 1) {
        setSchoolId(activeSchools[0].id);
      } else {
        setSchoolId("");
      }
    }
  }, [open, activeSchools.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    if (saving) return;
    onOpenChange?.(false);
  };

  const save = async () => {
    if (!schoolId) {
      toast.error("Pick a school/campus first");
      return;
    }
    setSaving(true);
    try {
      const r = await api.caseloadBulkAssignSchool({
        student_ids: studentIds,
        school_id: schoolId,
      });
      const data = r?.data || {};
      const ok = data.assigned_count || 0;
      const skipped = data.skipped_count || 0;
      const name = activeSchools.find((s) => s.id === schoolId)?.name || "campus";
      if (skipped) {
        toast.warning(
          `Assigned ${ok} to "${name}". ${skipped} skipped (cross-org or missing).`,
        );
      } else {
        toast.success(`Assigned ${ok} student${ok === 1 ? "" : "s"} to "${name}".`);
      }
      onOpenChange?.(false);
      onAssigned?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not assign campus");
    } finally {
      setSaving(false);
    }
  };

  const count = (studentIds || []).length;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="assign-campus-dialog"
        className="bg-white"
      >
        <DialogHeader>
          <DialogTitle>
            Assign {count} student{count === 1 ? "" : "s"} to a campus
          </DialogTitle>
          <DialogDescription>
            Sets the school/campus for the selected students. The
            update is reversible — assign them somewhere else any
            time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {activeSchools.length === 0 ? (
            <p className="text-[13px] text-[#7A3A2C]">
              You don't have any active schools/workspaces. Add one
              from Workspace Info before reassigning students.
            </p>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="assign-campus-school-select">
                School / campus
              </Label>
              <Select value={schoolId} onValueChange={setSchoolId}>
                <SelectTrigger
                  id="assign-campus-school-select"
                  data-testid="assign-campus-school-select"
                  className="bg-white border-[#E5E3DB]"
                >
                  <SelectValue placeholder="Pick one…" />
                </SelectTrigger>
                <SelectContent>
                  {activeSchools.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={saving}
            data-testid="assign-campus-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving || !schoolId || activeSchools.length === 0}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
            data-testid="assign-campus-confirm"
          >
            {saving ? "Assigning…" : `Assign ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
